import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import RunProgressPanel from 'components/run-progress/RunProgressPanel';
import { mapRunStepsToUi } from 'components/run-progress/mapRunStepsToUi';
import socketStoreSync from 'app/socketStoreSync';

import {
  getStoreSyncProcessStatusApi,
  downloadStoreSyncRunReportCsvApi,
} from 'features/store-synchronization/store-synchronization.api';
import { setSyncProcessStatusFromSocket } from 'features/store-synchronization/store-synchronization.slice';

const SYNC_SUMMARY_STATS = [
  { key: 'totalStores', label: 'Stores' },
  { key: 'succeededStores', label: 'Succeeded', color: 'green.500' },
  { key: 'failedStores', label: 'Failed stores', color: 'red.500' },
  { key: 'syncedListings', label: 'Listings synced', color: 'green.500' },
  { key: 'failedListings', label: 'Listing failures', color: 'red.500' },
];

const SyncProgressSection = () => {
  const dispatch = useDispatch();
  const { syncProcessStatus, isSyncRunning, downloadSyncReportStatus } = useSelector(
    (state) => state.storeSynchronization,
  );

  const displayIsRunning = isSyncRunning || Boolean(syncProcessStatus?.isRunning);
  const steps = useMemo(() => mapRunStepsToUi(syncProcessStatus), [syncProcessStatus]);
  const overallProgress = syncProcessStatus?.overallProgress ?? 0;
  const runSummary = syncProcessStatus?.runSummary ?? null;
  const hasReport = Boolean(syncProcessStatus?.hasReport);

  useEffect(() => {
    dispatch(getStoreSyncProcessStatusApi());
  }, [dispatch]);

  useEffect(() => {
    socketStoreSync.connect();
    const onRunState = (payload) => {
      dispatch(setSyncProcessStatusFromSocket(payload));
    };
    const onStatus = () => {
      dispatch(getStoreSyncProcessStatusApi());
    };

    socketStoreSync.on('store-sync-run-state', onRunState);
    socketStoreSync.on('store-sync-status', onStatus);

    return () => {
      socketStoreSync.off('store-sync-run-state', onRunState);
      socketStoreSync.off('store-sync-status', onStatus);
      socketStoreSync.disconnect();
    };
  }, [dispatch]);

  useEffect(() => {
    if (!displayIsRunning) return undefined;
    const interval = setInterval(() => {
      dispatch(getStoreSyncProcessStatusApi());
    }, 2000);
    return () => clearInterval(interval);
  }, [dispatch, displayIsRunning]);

  return (
    <RunProgressPanel
      title="Synchronization progress"
      steps={steps}
      overallProgress={overallProgress}
      isRunning={displayIsRunning}
      currentMessage={syncProcessStatus?.message ?? ''}
      runSummary={runSummary}
      summaryStats={SYNC_SUMMARY_STATS}
      hasReport={hasReport}
      onDownloadReport={() => dispatch(downloadStoreSyncRunReportCsvApi())}
      isDownloadingReport={downloadSyncReportStatus === 'loading'}
      showSummary={
        !displayIsRunning && runSummary && (runSummary.totalStores ?? 0) > 0
      }
    />
  );
};

export default SyncProgressSection;
